import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Upload,
  X,
  Save,
  Eye,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import { toast } from '../../hooks/use-toast';
import ReactQuill from '../../components/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  title: '',
  titleRu: '',
  description: '',
  descriptionRu: '',
  image: '',
  imageRu: '',
  buttonText: '',
  buttonTextRu: '',
  buttonUrl: '',
  isActive: false,
  showOnce: true,
  delaySeconds: 2,
};

const getId = (popup) => popup?.id || popup?._id;

const getImageSrc = (url) => {
  if (!url || typeof url !== 'string') return '';

  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('blob:') ||
    url.startsWith('data:')
  ) {
    return url;
  }

  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const stripHtml = (html) =>
  (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const PopupManagement = () => {
  const { getAuthHeaders } = useAdmin();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [popups, setPopups] = useState([]);

  // 'list' = tabel, 'form' = adăugare / editare
  const [view, setView] = useState('list');
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState(emptyForm);

  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchPopups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPopups = async () => {
    try {
      const res = await axios.get(`${API}/admin/popups`, getAuthHeaders());

      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.items || [];

      setPopups(list);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca popup-urile',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setView('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (popup) => {
    setEditingId(getId(popup));

    setFormData({
      title: popup.title || '',
      titleRu: popup.titleRu || '',
      description: popup.description || '',
      descriptionRu: popup.descriptionRu || '',
      image: popup.image || '',
      imageRu: popup.imageRu || '',
      buttonText: popup.buttonText || '',
      buttonTextRu: popup.buttonTextRu || '',
      buttonUrl: popup.buttonUrl || '',
      isActive: !!popup.isActive,
      showOnce: popup.showOnce !== false,
      delaySeconds: popup.delaySeconds ?? 2,
    });

    setView('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToList = () => {
    setView('list');
    setEditingId(null);
    setFormData(emptyForm);
  };

  // field = 'image' (RO) sau 'imageRu' (RU)
  const handleImageUpload = (e, field) => {
    const file = e.target.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, [field]: reader.result }));
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);

    try {
      if (editingId) {
        await axios.put(
          `${API}/admin/popups/${editingId}`,
          formData,
          getAuthHeaders()
        );
      } else {
        await axios.post(`${API}/admin/popups`, formData, getAuthHeaders());
      }

      toast({
        title: 'Succes',
        description: editingId
          ? 'Popup-ul a fost actualizat'
          : 'Popup-ul a fost adăugat',
      });

      await fetchPopups();
      backToList();
    } catch (error) {
      toast({
        title: 'Eroare',
        description:
          error.response?.data?.detail || 'Nu s-a putut salva popup-ul',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (popup) => {
    const id = getId(popup);

    try {
      await axios.put(
        `${API}/admin/popups/${id}`,
        { ...popup, isActive: !popup.isActive },
        getAuthHeaders()
      );

      setPopups((prev) =>
        prev.map((p) =>
          getId(p) === id ? { ...p, isActive: !popup.isActive } : p
        )
      );
    } catch (error) {
      toast({
        title: 'Eroare',
        description:
          error.response?.data?.detail || 'Nu s-a putut schimba statusul',
        variant: 'destructive',
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const id = getId(deleteTarget);

    try {
      await axios.delete(`${API}/admin/popups/${id}`, getAuthHeaders());

      setPopups((prev) => prev.filter((p) => getId(p) !== id));

      toast({
        title: 'Succes',
        description: 'Popup-ul a fost șters',
      });
    } catch (error) {
      toast({
        title: 'Eroare',
        description:
          error.response?.data?.detail || 'Nu s-a putut șterge popup-ul',
        variant: 'destructive',
      });
    } finally {
      setDeleteTarget(null);
    }
  };

  // Bloc reutilizabil pentru încărcarea imaginii (RO / RU)
  const renderImageBox = (field, label, uploadText) => {
    const value = formData[field];

    return (
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <label className="mb-3 block text-sm font-bold text-gray-700">
          {label}
        </label>

        {value ? (
          <div className="space-y-3">
            <div className="relative overflow-hidden rounded-2xl bg-gray-50">
              <img
                src={getImageSrc(value)}
                alt={label}
                className="h-64 w-full object-cover"
              />
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, [field]: '' }))
                }
                className="absolute right-3 top-3 rounded-full bg-red-500 p-2 text-white"
                aria-label="Elimină imaginea"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl bg-gray-100 px-4 py-3 text-sm font-bold text-gray-700 transition hover:bg-brand-50 hover:text-brand-700">
              <Upload className="h-4 w-4" />
              Schimbă imaginea
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, field)}
                className="hidden"
              />
            </label>
          </div>
        ) : (
          <label className="block cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 p-8 text-center transition hover:border-brand-500">
            <Upload className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            <span className="font-bold text-brand-600">{uploadText}</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, field)}
              className="hidden"
            />
          </label>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-brand-600" />
      </div>
    );
  }

  /* =====================================================
     LISTA (TABEL)
  ====================================================== */
  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight text-gray-900">
                Popup-uri website
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Gestionează popup-urile promoționale afișate pe website.
              </p>
            </div>

            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-5 py-3 font-bold text-white shadow-md shadow-brand-200 transition hover:bg-brand-600"
            >
              <Plus className="h-5 w-5" />
              Adaugă popup
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
          {popups.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-lg font-semibold text-gray-600">
                Nu există popup-uri create încă.
              </p>

              <button
                type="button"
                onClick={openCreate}
                className="mt-4 font-bold text-brand-600 hover:text-brand-700"
              >
                + Adaugă primul popup
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left">
                <thead className="bg-gray-50 text-xs font-extrabold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-5 py-4">Imagine</th>
                    <th className="px-5 py-4">Titlu</th>
                    <th className="px-5 py-4">Buton</th>
                    <th className="px-5 py-4">Delay</th>
                    <th className="px-5 py-4">O singură dată</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4 text-right">Acțiuni</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {popups.map((popup) => {
                    const id = getId(popup);
                    const img = getImageSrc(popup.image);
                    const imgRu = getImageSrc(popup.imageRu);

                    return (
                      <tr key={id} className="transition hover:bg-gray-50/60">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              {img ? (
                                <img
                                  src={img}
                                  alt=""
                                  className="h-14 w-20 rounded-xl object-cover"
                                />
                              ) : (
                                <div className="flex h-14 w-20 items-center justify-center rounded-xl bg-gray-100 text-xs font-semibold text-gray-400">
                                  Fără
                                </div>
                              )}
                              <span className="absolute -bottom-1 -left-1 rounded-md bg-gray-900 px-1.5 py-0.5 text-[10px] font-black text-white">
                                RO
                              </span>
                            </div>

                            {imgRu && (
                              <div className="relative">
                                <img
                                  src={imgRu}
                                  alt=""
                                  className="h-14 w-20 rounded-xl object-cover"
                                />
                                <span className="absolute -bottom-1 -left-1 rounded-md bg-brand-600 px-1.5 py-0.5 text-[10px] font-black text-white">
                                  RU
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="max-w-[260px] px-5 py-4">
                          <p className="truncate font-bold text-gray-900">
                            {popup.title || 'Fără titlu'}
                          </p>
                          {popup.description && (
                            <p className="mt-0.5 truncate text-sm text-gray-500">
                              {stripHtml(popup.description)}
                            </p>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-gray-700">
                            {popup.buttonText || '—'}
                          </p>
                          {popup.buttonUrl && (
                            <p className="max-w-[160px] truncate text-xs text-gray-400">
                              {popup.buttonUrl}
                            </p>
                          )}
                        </td>

                        <td className="px-5 py-4 text-sm font-semibold text-gray-700">
                          {popup.delaySeconds ?? 0}s
                        </td>

                        <td className="px-5 py-4 text-sm font-semibold text-gray-700">
                          {popup.showOnce !== false ? 'Da' : 'Nu'}
                        </td>

                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(popup)}
                            title="Click pentru a schimba statusul"
                            className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
                              popup.isActive
                                ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 hover:bg-brand-100'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {popup.isActive ? 'Activ' : 'Inactiv'}
                          </button>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(popup)}
                              className="inline-flex items-center gap-1.5 rounded-xl bg-gray-100 px-3 py-2 text-sm font-bold text-gray-700 transition hover:bg-brand-50 hover:text-brand-700"
                            >
                              <Pencil className="h-4 w-4" />
                              Editează
                            </button>

                            <button
                              type="button"
                              onClick={() => setDeleteTarget(popup)}
                              className="inline-flex items-center gap-1.5 rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-600 transition hover:bg-red-100"
                            >
                              <Trash2 className="h-4 w-4" />
                              Șterge
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* MODAL CONFIRMARE ȘTERGERE */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
              <h3 className="text-xl font-extrabold text-gray-900">
                Ștergi popup-ul?
              </h3>

              <p className="mt-2 text-sm text-gray-600">
                Popup-ul{' '}
                <span className="font-bold">
                  „{deleteTarget.title || 'Fără titlu'}”
                </span>{' '}
                va fi șters definitiv. Acțiunea nu poate fi anulată.
              </p>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="rounded-xl bg-gray-100 px-5 py-2.5 font-bold text-gray-700 transition hover:bg-gray-200"
                >
                  Anulează
                </button>

                <button
                  type="button"
                  onClick={confirmDelete}
                  className="rounded-xl bg-red-500 px-5 py-2.5 font-bold text-white transition hover:bg-red-600"
                >
                  Șterge
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* =====================================================
     FORMULAR (ADĂUGARE / EDITARE)
  ====================================================== */
  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={backToList}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-700 transition hover:bg-gray-200"
              aria-label="Înapoi la listă"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            <div>
              <h2 className="text-2xl font-extrabold tracking-tight text-gray-900">
                {editingId ? 'Editează popup' : 'Popup nou'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Configurează popup-ul promoțional afișat pe website.
              </p>
            </div>
          </div>

          <span
            className={`rounded-full px-4 py-2 text-sm font-bold ${
              formData.isActive
                ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {formData.isActive ? 'Activ' : 'Inactiv'}
          </span>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]"
      >
        <div className="space-y-6 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Titlu RO
              </label>
              <input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200"
                placeholder="ex: Ofertă specială"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Titlu RU
              </label>
              <input
                value={formData.titleRu}
                onChange={(e) =>
                  setFormData({ ...formData, titleRu: e.target.value })
                }
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200"
                placeholder="ex: Специальное предложение"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Descriere RO
              </label>

              <ReactQuill
                value={formData.description}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    description: value,
                  })
                }
                placeholder="Descriere popup..."
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Descriere RU
              </label>

              <ReactQuill
                value={formData.descriptionRu}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    descriptionRu: value,
                  })
                }
                placeholder="Описание popup..."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Text buton RO
              </label>
              <input
                value={formData.buttonText}
                onChange={(e) =>
                  setFormData({ ...formData, buttonText: e.target.value })
                }
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200"
                placeholder="Vezi oferta"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Text buton RU
              </label>
              <input
                value={formData.buttonTextRu}
                onChange={(e) =>
                  setFormData({ ...formData, buttonTextRu: e.target.value })
                }
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200"
                placeholder="Смотреть"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-gray-700">
              Link buton
            </label>
            <input
              value={formData.buttonUrl}
              onChange={(e) =>
                setFormData({ ...formData, buttonUrl: e.target.value })
              }
              className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200"
              placeholder="/catalog sau https://..."
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3">
              <span className="text-sm font-bold text-gray-700">Activ</span>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                className="h-5 w-5 accent-brand-500"
              />
            </label>

            <label className="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3">
              <span className="text-sm font-bold text-gray-700">
                Arată o singură dată
              </span>
              <input
                type="checkbox"
                checked={formData.showOnce}
                onChange={(e) =>
                  setFormData({ ...formData, showOnce: e.target.checked })
                }
                className="h-5 w-5 accent-brand-500"
              />
            </label>

            <div>
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Delay secunde
              </label>
              <input
                type="number"
                min="0"
                value={formData.delaySeconds}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    delaySeconds: Number(e.target.value),
                  })
                }
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200"
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={backToList}
              className="w-full rounded-2xl bg-gray-100 px-5 py-3 font-bold text-gray-700 transition hover:bg-gray-200 sm:w-auto"
            >
              Anulează
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex w-full flex-1 items-center justify-center gap-2 rounded-2xl bg-brand-500 px-5 py-3 font-bold text-white shadow-md shadow-brand-200 transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-5 w-5" />
              {saving
                ? 'Se salvează...'
                : editingId
                ? 'Salvează modificările'
                : 'Adaugă popup'}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {renderImageBox('image', 'Imagine popup (RO)', 'Încarcă imagine RO')}

          {renderImageBox(
            'imageRu',
            'Imagine popup (RU)',
            'Încarcă imagine RU'
          )}

          <p className="px-1 text-xs text-gray-500">
            Dacă nu încarci imagine pentru RU, pe site-ul în limba rusă se va
            afișa imaginea RO.
          </p>

          <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-700">
              <Eye className="h-4 w-4 text-brand-600" />
              Preview rapid (RO)
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-100">
              {formData.image && (
                <img
                  src={getImageSrc(formData.image)}
                  alt=""
                  className="h-36 w-full object-cover"
                />
              )}
              <div className="p-4">
                <p className="text-xs font-extrabold uppercase tracking-wide text-brand-600">
                  AgroSmart
                </p>
                <h3 className="mt-2 text-xl font-black text-gray-900">
                  {formData.title || 'Titlu popup'}
                </h3>

                {formData.description ? (
                  <div
                    className="mt-2 text-sm text-gray-500"
                    dangerouslySetInnerHTML={{ __html: formData.description }}
                  />
                ) : (
                  <p className="mt-2 text-sm text-gray-500">
                    Descrierea popup-ului va apărea aici.
                  </p>
                )}

                {formData.buttonText && (
                  <div className="mt-4 inline-flex rounded-full bg-brand-500 px-4 py-2 text-sm font-bold text-white">
                    {formData.buttonText}
                  </div>
                )}
              </div>
            </div>
          </div>

          {(formData.imageRu || formData.titleRu) && (
            <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-700">
                <Eye className="h-4 w-4 text-brand-600" />
                Preview rapid (RU)
              </div>

              <div className="overflow-hidden rounded-2xl border border-gray-100">
                {(formData.imageRu || formData.image) && (
                  <img
                    src={getImageSrc(formData.imageRu || formData.image)}
                    alt=""
                    className="h-36 w-full object-cover"
                  />
                )}
                <div className="p-4">
                  <p className="text-xs font-extrabold uppercase tracking-wide text-brand-600">
                    AgroSmart
                  </p>
                  <h3 className="mt-2 text-xl font-black text-gray-900">
                    {formData.titleRu || formData.title || 'Заголовок'}
                  </h3>

                  {(formData.descriptionRu || formData.description) && (
                    <div
                      className="mt-2 text-sm text-gray-500"
                      dangerouslySetInnerHTML={{
                        __html:
                          formData.descriptionRu || formData.description,
                      }}
                    />
                  )}

                  {(formData.buttonTextRu || formData.buttonText) && (
                    <div className="mt-4 inline-flex rounded-full bg-brand-500 px-4 py-2 text-sm font-bold text-white">
                      {formData.buttonTextRu || formData.buttonText}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default PopupManagement;